import { loadable } from '@change-corgi/core-react/loadable';

import type { AppRoute } from '#src/shared/routes';

import { createRoutes, handleRecruiter, Loading, petitionSlugCanonicalRedirect } from '#src/app/shared/routes';

import { resolvePetitionL10nForBotsFromSlugParam } from './shared/resolvePetitionL10nForBotsFromSlugParam';
import { shareUrlCanonicalRedirect } from './shared/shareUrlCanonicalRedirect';

export const ROUTES: readonly AppRoute[] = [
	/**
	 * Canonical petition route
	 */
	{
		id: 'petition-details',
		path: '/p/:slug',
		component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
			exportName: 'PetitionDetails',
			fallback: Loading,
		}),
		frame: true,
		beforeServerRender: async (context) => {
			const { slug } = context.params;
			if (slug) await handleRecruiter(context, slug);
			await petitionSlugCanonicalRedirect(context);
		},
		ssr: {
			enabled: true,
			seo: true,
			resolveL10n: resolvePetitionL10nForBotsFromSlugParam,
			/**
			 * IMPORTANT!
			 *
			 * This SSR config makes adds restrictions on how user data can be accessed within the page.
			 *
			 * Guest: The page is cached server-side. This means that any components rendering server-side MUST NOT
			 * request user-specific information. Our session utilities will throw an error to prevent this mistake. Any
			 * user data MUST be loaded & accessed through pageUserData only.
			 *
			 * Non-guest (logged-in or identified): The page is not cached, so the above is not a problem. But this
			 * means if you only test while logged in you may overlook an error in the guest experience.
			 *
			 * See doc/server-basics.md for more info.
			 */
			config: ({ loginState }) => {
				// Cache for guests
				if (loginState === 'GUEST') {
					return {
						disallowUserData: true,
						cache: {
							enabled: true,
							lifetimeInSeconds: 1 * 60, // 1min
							queryParams: ['expired_session'],
						},
					};
				}
				// Avoid caching for non-guest - mainly so starters & staff see updates immediately.
				return {};
			},
		},
	},
	/**
	 * Bot-share-routes
	 */
	...createRoutes(
		{
			frame: true,
			beforeServerRender: shareUrlCanonicalRedirect,
			ssr: {
				enabled: true,
				seo: true, // enabled so that twitter/FB can generate a preview
				resolveL10n: resolvePetitionL10nForBotsFromSlugParam,
				config: {
					disallowUserData: true,
				},
			},
		},
		[
			{
				// This route renders the same component as the canonical route. The main component
				// includes the right meta tags when a petition is a victory.
				id: 'petition-details-victory',
				path: '/p/:slug/share_victory',
				component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
					exportName: 'PetitionDetails',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-custom-preview-link-sfs',
				path: '/p/:slug/sfs/:shareChannel/:sharerUserId/:previewId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
					exportName: 'PetitionDetailsShareForStarters',
					fallback: Loading,
				}),
			},
			{
				id: 'petition-details-bandit-sfs',
				path: '/p/:slug/sfs/:shareChannel/:sharerUserId',
				component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
					exportName: 'PetitionDetailsShareForStarters',
					fallback: Loading,
				}),
			},

			{
				id: 'petition-details-bandit-fbog',
				path: ['/p/:slug/fbog/:sharerUserId', '/p/:slug/fbog/:sharerUserId/bsv'],
				component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
					exportName: 'PetitionDetailsFacebook',
					fallback: Loading,
				}),
			},
			{
				// Crawlers get the arm image of psf_og_image_experiment. Permanent: shared links keep these paths after the
				// experiment ends. No /bsv variant, because the arm image replaces the /bsv one.
				id: 'petition-details-og-image',
				path: ['/p/:slug/img/:variant', '/p/:slug/fbog/:sharerUserId/img/:variant'],
				component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
					exportName: 'PetitionDetailsOgImage',
					fallback: Loading,
				}),
			},
			/**
			 * /bsv suffix: signals that the share asset should be used as og:image.
			 * Generated by PSF share flows when psf_share_assets_enabled FCM is on.
			 * Each entry point checks context.path.endsWith('/bsv') to set bestShareVisual.
			 * This covers the canonical /p/:slug/bsv path; the /bsv variant for each
			 * bot share route is merged into its parent route above via path: string[].
			 */
			{
				id: 'petition-details-bsv',
				path: '/p/:slug/bsv',
				component: loadable(async () => import(/* webpackChunkName: "pages/petitionDetails" */ '../index'), {
					exportName: 'PetitionDetails',
					fallback: Loading,
				}),
			},
		],
	),
];
